/**
 * @sunflowerlab
 * @author Ashish Kumar
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiUrl, APP_ROUTES, AppConstants } from '../@shared/constants';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { User } from '../@shared/models/user.model';
import { Router } from '@angular/router';
import { OGantryHttpResponse } from '@shared/models';
import { ReleaseVersion, RolePreferences, SelfResponse, SocialAuthRequest, SocialLoginOption } from './auth.model';
import { MenuConfigService } from '@shared/services/menu-config.service';
import { SocialNewAuthResponse } from '.';
import { AdministrationService } from '@entities/administration/administration.service';
import { isKeyPermitted, PermissionMap } from '@shared/models/permission-map';
import { NavigationConfig } from '@shared/_config/naivgation.config';
import { Utility } from '@shared/utils/utils';
export interface AuthResponse {
  access_token: string;
  renewal_token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isClearMenuList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private self: SelfResponse = null;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly menuConfigService: MenuConfigService,
    private readonly adminService: AdministrationService
  ) {}

  login(credentials): Observable<string> {
    const data = {
      user: {
        email: credentials.email,
        password: credentials.password
      }
    };
    return this.http.post(ApiUrl.login, data).pipe(map((res) => this.authenticateSuccess(res)));
  }

  authenticateSuccess(resp) {
    const bearerToken = resp.data.access_token;
    if (bearerToken && window.localStorage) {
      this.setAuthTokens(resp);
      return bearerToken;
    }
  }

  register(registerUser): Observable<string> {
    return this.http.post<string>(ApiUrl.register, registerUser);
  }

  logout() {
    this.deleteSession().subscribe();
    this.stopRefreshTokenTimer();
    localStorage.removeItem(AppConstants.authenticationToken);
    localStorage.removeItem(AppConstants.refreshToken);
    sessionStorage.removeItem(AppConstants.userKey);
    localStorage.removeItem('userEmail');
    localStorage.removeItem('cacheFilters');
    localStorage.removeItem('selectedColumnsArray');
    localStorage.removeItem('selectedColumnMappingForTimeSheet');
    this.adminService.setExtendedFiled(undefined);
    localStorage.clear();
    this.clearSelf();
    this.isClearMenuList$.next(true);
    this.router.navigateByUrl(APP_ROUTES.LOGIN);
  }

  deleteSession() {
    return this.http.delete(ApiUrl.login);
  }

  getAccount(): Observable<User> {
    return this.http.get<User>(ApiUrl.account);
  }

  forgetPasswordInit(email: string): Observable<void> {
    return this.http.post<void>(ApiUrl.forgetPassword.init, { email });
  }

  refreshToken() {
    return this.http.post<OGantryHttpResponse<AuthResponse>>(ApiUrl.renewSession, {}, { withCredentials: true }).pipe(
      map((user) => {
        this.setAuthTokens(user);
        return user;
      })
    );
  }

  setAuthTokens(user) {
    localStorage.setItem(AppConstants.authenticationToken, user.data.access_token);
    localStorage.setItem(AppConstants.refreshToken, user.data.renewal_token);
  }

  private refreshTokenTimeout;

  private startRefreshTokenTimer(userToken) {
    const expiringInMinutes = 30;
    const expireDate = new Date();
    expireDate.setMinutes(expireDate.getMinutes() + expiringInMinutes);
    const expires = new Date(expireDate);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshToken().subscribe();
    }, timeout);
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  socialLogin(redirect_uri: string, socialLoginType: SocialLoginOption): Observable<OGantryHttpResponse<SocialNewAuthResponse>> {
    let url = '';
    switch (socialLoginType) {
      case SocialLoginOption.GOOGLE:
        url = `${ApiUrl.googleAuth}/new`;
        break;
      case SocialLoginOption.GITHUB:
        url = `${ApiUrl.githubAuth}/new`;
        break;
      case SocialLoginOption.MICROSOFT:
        url = `${ApiUrl.microsoftAuth}/new`;
        break;
    }
    return this.http.get<OGantryHttpResponse<SocialNewAuthResponse>>(url, { params: { redirect_uri } });
  }

  completeSocialLogin(googleAuthReq: SocialAuthRequest, socialLoginType: SocialLoginOption): Observable<OGantryHttpResponse<AuthResponse>> {
    switch (socialLoginType) {
      case SocialLoginOption.GOOGLE:
        return this.http.post<OGantryHttpResponse<AuthResponse>>(`${ApiUrl.googleAuth}/callback`, { data: googleAuthReq }).pipe(map((res) => this.authenticateSuccess(res)));
      case SocialLoginOption.GITHUB:
        return this.http.post<OGantryHttpResponse<AuthResponse>>(`${ApiUrl.githubAuth}/callback`, { data: googleAuthReq }).pipe(map((res) => this.authenticateSuccess(res)));
      case SocialLoginOption.MICROSOFT:
        return this.http.post<OGantryHttpResponse<AuthResponse>>(`${ApiUrl.microsoftAuth}/callback`, { data: googleAuthReq }).pipe(map((res) => this.authenticateSuccess(res)));
    }
  }

  /**
   * @whatitdoes
   * Checks if any of the @param permissionModules ("View X" / "Manage X" keys) is granted by the
   * permissions map the server returned in get_self. Signature unchanged for the route guard,
   * the hasAnyPermission directive and the menu filter.
   * @returns true if the user is authorized to access the page/route.
   */
  async isPermittedAction(permissionModules): Promise<boolean> {
    if (!permissionModules || !permissionModules.length) {
      // If a route or page doesn't have permissionModules in its data in route config, that becomes a global route.
      return false;
    }
    const permissions = this.permissions;
    return permissionModules.some((key) => isKeyPermitted(permissions, key));
  }

  get permissions(): PermissionMap {
    return this.self?.permissions || {};
  }

  get roleName(): string | null {
    return this.self?.role_name || null;
  }

  get preferences(): RolePreferences {
    return this.self?.preferences || {};
  }

  get landingPage(): string | null {
    return this.self?.preferences?.web?.navigation?.landing_page || null;
  }

  /**
   * Returns the stored get_self response. The API is called only when nothing is stored yet
   * (app start-up) or when force is true (after login, or after the user's own role changed).
   */
  async loadSelf(force = false): Promise<SelfResponse> {
    if (this.self && !force) {
      return this.self;
    }
    if (!localStorage.getItem(AppConstants.authenticationToken)) {
      return this.emptySelf();
    }
    try {
      const res = await this.getUserInfo().toPromise();
      const data: Partial<SelfResponse> = res?.data || {};
      this.self = {
        user: data.user || null,
        permissions: data.permissions || {},
        role_name: data.role_name || null,
        preferences: data.preferences || {}
      };
      if (data.user?.email) {
        localStorage.setItem('userEmail', data.user.email);
      }
      this.menuConfigService.onConfigUpdated$.next(true);
    } catch {
      return this.self || this.emptySelf();
    }
    return this.self;
  }

  async updatePermissionsInCache(): Promise<User> {
    const self = await this.loadSelf(true);
    return { user: self.user };
  }

  getUserInfo(): Observable<OGantryHttpResponse<SelfResponse>> {
    return this.http.get<OGantryHttpResponse<SelfResponse>>(ApiUrl.users);
  }

  async getCurrentUser(): Promise<User> {
    const self = await this.loadSelf();
    return { user: self.user };
  }

  async getLandingPageRoute(): Promise<string> {
    await this.loadSelf();
    const landingPage = this.landingPage;
    if (!landingPage) {
      return APP_ROUTES.MANAGE_PROJECT;
    }
    const navigationItem = this.findNavigationItem(landingPage);
    if (navigationItem?.permissionModules?.length) {
      const isActionPermitted = await this.isPermittedAction(navigationItem.permissionModules);
      return isActionPermitted ? Utility.withLeadingSlash(landingPage) : APP_ROUTES.FORBIDDEN;
    }
    return Utility.withLeadingSlash(landingPage);
  }

  findNavigationItem(page: string): { title: string; page: string; permissionModules?: string[] } | null {
    const target = Utility.normalizePagePath(page);
    const items = new NavigationConfig().defaults?.aside?.items || [];
    for (const item of items) {
      for (const sub of item?.submenu || []) {
        if (Utility.normalizePagePath(sub?.page) === target) {
          return sub;
        }
      }
    }
    return null;
  }

  private emptySelf(): SelfResponse {
    return { user: null, permissions: {}, preferences: {}, role_name: null };
  }

  private clearSelf(): void {
    this.self = null;
  }

  getBackendBuildVersion(): Observable<OGantryHttpResponse<ReleaseVersion>> {
    return this.http.get<OGantryHttpResponse<ReleaseVersion>>(ApiUrl.backendBuildVersion);
  }

  checkAuthSessionStatus(): Observable<any> {
    return this.http.get(ApiUrl.checkAuthSessionStatus);
  }

  updateNavigationDetail() {}
}
